<template>
	<div class="w-full h-full py-4 flex flex-col items-center gap-5">
		<Section
			v-model="open.patient_data"
			title="DADOS DO PACIENTE"
		>
			<div class="grid grid-cols-12 gap-4">
				<div class="col-span-8 flex items-center">
					<FormGroup
						v-model="form.patient_data.unknown"
						entity-key="unknown"
						checkbox-label="DESCONHECIDO"
						input-type="checkbox"
						class="w-full lg:w-2/12"
						:omit-info="true"
					/>
					<FormGroup
						v-model="form.patient_data.uncapable"
						entity-key="uncapable"
						checkbox-label="INCAPAZ"
						input-type="checkbox"
						class="w-full lg:w-2/12"
						:omit-info="true"
					/>
					<FormGroup
						v-model="form.patient_data.newborn"
						entity-key="newborn"
						checkbox-label="RECÉM-NASCIDO"
						input-type="checkbox"
						class="w-full lg:w-2/12"
						:omit-info="true"
					/>
				</div>

				<FormGroup
					v-model="form.patient_data.rh"
					class="col-start-11 col-span-2"
					:readonly="!!form.patient_data.rh"
					:disabled="!form.patient_data.rh"
					label="PRONTUÁRIO"
					placeholder=""
				/>
			</div>

			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.patient_data.cpf"
					formatter="cpf"
					class="col-span-3"
					label="CPF"
					placeholder="000.000.000-00"
					:required="obligations?.cpf"
					:input-classes="[{ 'field-error': !validations.patient_data.cpf }]"
					:input-type="withQuery ? 'doc-select' : 'text'"
					entity-key="cpf"
					:options="
						patients
							.filter(p => p.cpf)
							.map(p => ({
								...p,
								label: `CPF: ${p.cpf} - ${p.patient_name}`,
								value: p.id,
							}))
					"
					@select="SelectPatient"
				/>
				<FormGroupRG
					class="col-span-5"
					:required="obligations?.rg"
					:rg="form.patient_data.rg ?? ''"
					:uf="form.patient_data.rg_uf ?? ''"
					:options-doc="
						patients
							.filter(p => p.rg)
							.map(p => ({
								...p,
								label: `RG: ${p.rg}${p.rg_uf} - ${p.patient_name}`,
								value: p.id,
							}))
					"
					:options-uf="STATES"
					entity-key="rg"
					:input-classes="[
						{ 'field-error': !validations.patient_data.rg || !validations.patient_data.rg_uf },
					]"
					@input-doc="form.patient_data.rg = $event"
					@input-uf="form.patient_data.rg_uf = $event"
					@select-doc="SelectPatient"
				/>
				<FormGroup
					v-model="form.patient_data.cns"
					class="col-span-4"
					label="CNS"
					formatter="numbers"
					placeholder="Nº DO CNS"
					:required="obligations?.cns"
					:input-classes="[{ 'field-error': !validations.patient_data.cns }]"
					:input-type="withQuery ? 'doc-select' : 'text'"
					entity-key="cns"
					:options="
						patients
							.filter(p => p.cns)
							.map(p => ({
								...p,
								label: `CNS: ${p.cns} - ${p.patient_name}`,
								value: p.id,
							}))
					"
					@select="SelectPatient"
				>
					<template #tooltip>
						<ul class="info-modal-list">
							<li class="info-modal-item">
								CARTÃO NACIONAL DE SAÚDE (TAMBÉM CONHECIDO COMO CARTÃO DO SUS)
							</li>
						</ul>
					</template>
				</FormGroup>
			</div>

			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.patient_data.patient_name"
					class="col-span-8"
					label="NOME DO PACIENTE"
					formatter="letters"
					:max-length="150"
					placeholder="DIGITE O NOME DO PACIENTE"
					:required="obligations?.patient_name"
					:input-classes="[
						{
							'field-error': !validations.patient_data.patient_name,
						},
					]"
					:input-type="withQuery ? 'name-select' : 'text'"
					:options="
						patients.map(p => ({
							id: p.id,
							label: p.patient_name,
							value: p.id,
						}))
					"
					@select="SelectPatient"
				/>
				<FormGroup
					v-model="form.patient_data.birth_date"
					input-type="date"
					class="col-span-2"
					label="DATA DE NASCIMENTO"
					placeholder="DD/MM/AAAA"
					:required="obligations?.birth_date"
					:input-classes="[
						{
							'field-error': !validations.patient_data.birth_date,
						},
					]"
				/>
				<FormGroup
					v-model="form.patient_data.sex"
					input-type="select"
					class="col-span-2"
					label="SEXO"
					:options="SEX_OPTIONS"
					placeholder="SELECIONE"
					:required="obligations?.sex"
					:input-classes="[{ 'field-error': !validations.patient_data.sex }]"
				/>
			</div>

			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.patient_data.phone"
					class="col-span-3"
					label="TELEFONE"
					formatter="phone"
					placeholder="(00) 00000-0000"
					:required="obligations?.phone"
					:input-classes="[{ 'field-error': !validations.patient_data.phone }]"
				/>
				<FormGroup
					v-model="form.patient_data.email"
					formatter="email"
					class="col-span-9"
					label="E-MAIL"
					placeholder="NOME@EXEMPLO.COM"
					:required="obligations?.email"
					:input-classes="[{ 'field-error': !validations.patient_data.email }]"
				/>
			</div>

			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.patient_data.social_name"
					class="col-span-10"
					label="NOME SOCIAL"
					formatter="letters"
					:max-length="150"
					placeholder="NOME SOCIAL"
				>
					<template #tooltip>
						<ul class="info-modal-list">
							<li class="info-modal-item">
								NOME ESCOLHIDO POR PESSOAS TRANS E NÃO BINÁRIAS PARA REFLETIR SUA IDENTIDADE DE GÊNERO
							</li>
						</ul>
					</template>
				</FormGroup>
				<FormGroup
					v-model="form.patient_data.genre"
					input-type="select"
					class="col-span-2"
					label="GÊNERO"
					:options="GENRE_OPTIONS"
					placeholder="SELECIONE"
				/>
			</div>

			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.patient_data.mother_name"
					class="col-span-12"
					label="NOME DA MÃE"
					formatter="letters"
					:max-length="150"
					placeholder="NOME DA MÃE"
					:required="obligations?.mother_name"
					:input-classes="[
						{
							'field-error': !validations.patient_data.mother_name,
						},
					]"
				/>
			</div>
		</Section>

		<Section
			v-model="open.address"
			title="ENDEREÇO"
		>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.address.cep"
					class="col-span-4"
					label="CEP"
					formatter="cep"
					placeholder="00000-000"
					:required="obligations?.cep"
					:input-classes="[{ 'field-error': !validations.address.cep }]"
				/>
				<FormGroup
					v-model="form.address.street"
					class="col-span-8"
					label="LOGRADOURO"
					placeholder="EX: RUA DAS FLORES, 123"
					:required="obligations?.street"
					:input-classes="[
						{
							'field-error': !validations.address.street,
						},
					]"
				/>
			</div>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.address.neighborhood"
					class="col-span-4"
					label="BAIRRO"
					placeholder="EX: CENTRO"
					:required="obligations?.neighborhood"
					:input-classes="[{ 'field-error': !validations.address.neighborhood }]"
				/>
				<FormGroup
					v-model="form.address.city"
					class="col-span-6"
					label="MUNICÍPIO"
					placeholder="EX: SÃO PAULO"
					:required="obligations?.city"
					:input-classes="[{ 'field-error': !validations.address.city }]"
				/>
				<FormGroup
					v-model="form.address.state"
					input-type="select"
					class="col-span-2"
					label="UF"
					:options="STATES"
					placeholder="UF"
					:required="obligations?.state"
					:input-classes="[{ 'field-error': !validations.address.state }]"
				/>
			</div>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.address.complement"
					class="col-span-12"
					label="COMPLEMENTO"
					placeholder="EX: APTO 42, BLOCO B"
					:required="obligations?.complement"
					:input-classes="[{ 'field-error': !validations.address.complement }]"
				/>
			</div>
		</Section>

		<Section
			v-model="open.additional_data"
			title="DADOS COMPLEMENTARES"
		>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.additional_data.phone_2"
					class="col-span-4"
					label="TELEFONE 2"
					formatter="phone"
					placeholder="(00) 00000-0000"
					:required="obligations?.phone_2"
					:input-classes="[{ 'field-error': !validations.additional_data.phone_2 }]"
				/>
				<FormGroup
					v-model="form.additional_data.phone_3"
					class="col-span-4"
					label="TELEFONE 3"
					formatter="phone"
					placeholder="(00) 00000-0000"
					:required="obligations?.phone_3"
					:input-classes="[{ 'field-error': !validations.additional_data.phone_3 }]"
				/>
				<FormGroup
					v-model="form.additional_data.scholarship"
					input-type="select"
					class="col-span-4"
					label="ESCOLARIDADE"
					placeholder="SELECIONE"
					:required="obligations?.scholarship"
					:options="SCHOLARSHIPS"
					:input-classes="[
						{
							'field-error': !validations.additional_data.scholarship,
						},
					]"
				/>
			</div>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.additional_data.race"
					input-type="select"
					class="col-span-6"
					label="RAÇA"
					placeholder="SELECIONE"
					:required="obligations?.race"
					:options="RACES"
					:input-classes="[{ 'field-error': !validations.additional_data.race }]"
				/>
				<FormGroup
					v-model="form.additional_data.religion"
					input-type="select"
					class="col-span-6"
					label="RELIGIÃO"
					placeholder="SELECIONE"
					:required="obligations?.religion"
					:options="RELIGIONS"
					:input-classes="[
						{
							'field-error': !validations.additional_data.religion,
						},
					]"
				/>
			</div>
			<div class="grid grid-cols-12 gap-4">
				<FormGroup
					v-model="form.additional_data.observation"
					input-type="textarea"
					class="col-span-12"
					label="OBSERVAÇÃO"
					placeholder=""
				/>
			</div>
		</Section>

		<div
			v-if="['confirmation', 'modal'].includes(origin)"
			id="modal-webcam"
			class="modal fade"
		>
			<div class="modal-dialog mw-100 d-flex justify-content-center align-items-center">
				<div class="modal-content">
					<button
						id="close-modal"
						class="btn btn-light btn-sm"
						@click="ClosePhotoModal"
					>
						<i class="fal fa-times"></i>
					</button>
					<div class="modal-header px-5 py-4 mt-5 mt-sm-1">
						<h4 class="font-20 m-0 text-center">Registrar a foto do paciente</h4>
					</div>
					<div class="modal-body d-flex flex-column align-items-center">
						<div
							v-if="webcam.snap"
							id="img-snapped"
						>
							<img
								:src="webcam.snap"
								width="100%"
								alt="Foto do paciente"
							/>
						</div>
						<WebCam
							v-show="!webcam.snap"
							id="webcam"
							ref="webcam"
							screenshot-format="image/png"
							:device-id="deviceId"
							width="100%"
							height="100%"
							@cameras="onCameras"
							@camera-change="onCameraChange"
						>
						</WebCam>

						<span
							v-if="!camera && !webcam.snap"
							class="align-self-center"
						>
							Aguarde a imagem da câmera aparecer...
						</span>
						<button
							class="btn btn-light my-4"
							:disabled="!webcam.ready || !edit"
							@click="SnapPhoto"
						>
							{{ webcam.snap ? 'Tirar outra foto' : 'Tirar foto' }}
						</button>
						<span class="mb-4 text-success"
							>* Para aceitar a foto que foi tirada basta fechar essa janela *
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	const _ = require('lodash')
	import moment from 'moment'
	import Helpers from '../../../../helpers'
	const helpers = new Helpers()

	import Swal from 'sweetalert2'
	import { mapGetters, mapActions } from 'vuex'
	import { WebCam } from 'vue-web-cam'
	import { useConfirmation, useError } from '@/composables'
	import FormGroupRG from '@/components/shared/layout/FormGroupRG'
	export default {
		name: 'PatientForm',
		components: {
			WebCam,
			FormGroupRG,
		},
		props: {
			withQuery: {
				type: Boolean,
				default: false,
			},
			patient: {
				type: Object,
				default: null,
			},
			origin: {
				type: String,
				default: 'pre_appointment',
			},
			edit: {
				type: Boolean,
				default: false,
			},
			baseName: {
				type: String,
				default: null,
			},
		},
		data() {
			return {
				submitted: false,
				exihibit_avatar: '',
				patients: [],
				form: {
					id: null,
					avatar: '',
					patient_code: '',
					doc_id: null,
					tipo: '',
					numero: '',
					patient_data: {
						unknown: false,
						uncapable: false,
						newborn: false,
						rh: '',
						cpf: '',
						rg: '',
						rg_uf: '',
						cns: '',
						patient_name: '',
						birth_date: '',
						sex: '',
						phone: '',
						email: '',
						social_name: '',
						genre: '',
						mother_name: '',
					},
					address: {
						cep: '',
						street: '',
						neighborhood: '',
						city: '',
						state: '',
						complement: '',
					},
					additional_data: {
						phone_2: '',
						phone_3: '',
						scholarship: '',
						// profissao: '',
						race: '',
						religion: '',
						observation: '',
					},
				},
				open: {
					patient_data: true,
					address: false,
					additional_data: false,
				},
				exists_doc: {
					cpf: false,
					rg: false,
					cns: false,
				},
				webcam: {
					ready: false,
					mode: 'user',
					snap: null,
				},
				camera: null,
				deviceId: null,
				devices: [],
				SEX_OPTIONS: [
					{ value: 'f', label: 'FEMININO' },
					{ value: 'm', label: 'MASCULINO' },
					{ value: 'x', label: 'INDETERMINADO' },
				],
				GENRE_OPTIONS: [
					{ value: 'f', label: 'FEMININO' },
					{ value: 'm', label: 'MASCULINO' },
					{ value: 'x', label: 'NÃO INFORMADO' },
				],
				SCHOLARSHIPS: [
					{ value: 'nenhum', label: 'SEM INSTRUÇÃO' },
					{
						value: 'fundamental.incompleto',
						label: 'ENSINO FUNDAMENTAL INCOMPLETO',
					},
					{
						value: 'fundamental.completo',
						label: 'ENSINO FUNDAMENTAL COMPLETO',
					},
					{
						value: 'medio.incompleto',
						label: 'ENSINO MÉDIO INCOMPLETO',
					},
					{ value: 'medio.completo', label: 'ENSINO MÉDIO COMPLETO' },
					{
						value: 'superior.incompleto',
						label: 'ENSINO SUPERIOR INCOMPLETO',
					},
					{
						value: 'superior.completo',
						label: 'ENSINO SUPERIOR COMPLETO',
					},
					{ value: 'pos.completo', label: 'PÓS-GRADUAÇÃO' },
					{ value: 'mestrado.completo', label: 'MESTRADO' },
					{ value: 'doutorado.completo', label: 'DOUTORADO' },
				],
				RACES: [
					{ value: 'white', label: 'BRANCA' },
					{ value: 'black', label: 'PRETA' },
					{ value: 'brown', label: 'PARDA' },
					{ value: 'yellow', label: 'AMARELA' },
					{ value: 'indigenous', label: 'INDÍGENA' },
				],
				RELIGIONS: [
					{ value: 'catolic', label: 'CATÓLICA' },
					{ value: 'evangelical', label: 'EVANGÉLICA' },
					{ value: 'spiritist', label: 'ESPÍRITA' },
					{ value: 'jehovah', label: 'TESTEMUNHA DE JEOVA' },
					{ value: 'umbanda', label: 'UMBANDA' },
					{ value: 'candomble', label: 'CANDOMBLÉ' },
					{ value: 'buddhist', label: 'BUDISTA' },
					{ value: 'jewish', label: 'JUDAICA' },
					{ value: 'other', label: 'OUTRA' },
					{ value: 'none', label: 'SEM RELIGIÃO' },
					{ value: 'not_informed', label: 'NÃO INFORMADA' },
				],
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id', 'requester_selected_object']),
			atLeastOne() {
				return !(this.form.rg || this.form.sus || this.form.cpf)
			},
			haveRG() {
				return !!this.form.rg
			},
			device: function () {
				return this.devices.find(n => n.deviceId === this.deviceId)
			},
			base64OrCDNImage() {
				if (!this.form.avatar) return false

				if (this.form.avatar instanceof File) return false

				let base_text = this.form.avatar.substr(0, 21)
				let url_text = this.form.avatar.substr(0, 7)
				return (
					base_text == 'data:image/png;base64' ||
					base_text == 'data:image/jpg;base64' ||
					base_text == 'data:image/jpeg;base6' ||
					url_text == 'http://'
				)
			},
			obligations() {
				if (this.form.patient_data.unknown) {
					return null
				}

				if (this.origin === 'pre_appointment') {
					return {
						patient_name: true,
						phone: this.requester_selected_object?.instance?.config?.messages,
					}
				}

				const organizationObligations = {
					...this.requester_selected_object?.instance?.config?.register_obligations,
					patient_name: true,
					phone: this.requester_selected_object?.instance?.config?.messages,
				}

				if (this.form.patient_data.newborn) {
					return {
						...organizationObligations,
						cpf: false,
						rg: false,
						cns: false,
					}
				}

				return organizationObligations
			},
			isCpfValid() {
				if (this.obligations?.cpf && !this.form.patient_data.cpf) return false

				const validCpf = window.helpers.ValidaCPF(this.form.patient_data.cpf)
				if (this.form.patient_data.cpf && !validCpf) return false

				if (this.exists_doc.cpf) return false

				return true
			},
			isRgValid() {
				if (this.obligations?.rg && !this.form.patient_data.rg) return false

				if (this.form.patient_data.rg_uf && !this.form.patient_data.rg) return false

				if (this.exists_doc.rg) return false

				return true
			},
			isRgUfValid() {
				if (this.obligations?.rg && !this.form.patient_data.rg_uf) return false

				if (this.form.patient_data.rg && !this.form.patient_data.rg_uf) return false

				if (this.exists_doc.rg) return false

				return true
			},
			isCnsValid() {
				if (this.obligations?.cns && !this.form.patient_data.cns) return false
				if (this.exists_doc.cns) return false

				return true
			},
			isPatientNameValid() {
				if (this.obligations?.patient_name && !this.form.patient_data.patient_name) return false
				return true
			},
			isBirthDateValid() {
				if (this.obligations?.birth_date && !this.form.patient_data.birth_date) return false
				if (moment(this.form.patient_data.birth_date, 'YYYY-MM-DD').isAfter(moment())) return false
				return true
			},
			isSexValid() {
				if (this.obligations?.sex && !this.form.patient_data.sex) return false
				return true
			},
			isPhoneValid() {
				if (this.obligations?.phone && !this.form.patient_data.phone) return false
				if (this.form.patient_data.phone && !this.ValidatePhone(this.form.patient_data.phone)) return false
				return true
			},
			isEmailValid() {
				if (this.obligations?.email && !this.form.patient_data.email) return false

				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
				const validEmail = emailRegex.test(this.form.patient_data.email)
				if (this.form.patient_data.email && !validEmail) return false

				return true
			},
			isMotherNameValid() {
				if (this.obligations?.mother_name && !this.form.patient_data.mother_name) return false
				return true
			},
			isCepValid() {
				if (this.obligations?.cep && !this.form.address.cep) return false
				if (this.form.address.cep && !/^\d{5}-\d{3}$/.test(this.form.address.cep)) return false
				return true
			},
			isStreetValid() {
				if (this.obligations?.street && !this.form.address.street) return false
				return true
			},
			isNeighborhoodValid() {
				if (this.obligations?.neighborhood && !this.form.address.neighborhood) return false
				return true
			},
			isCityValid() {
				if (this.obligations?.city && !this.form.address.city) return false
				return true
			},
			isStateValid() {
				if (this.obligations?.state && !this.form.address.state) return false
				return true
			},
			isComplementValid() {
				if (this.obligations?.complement && !this.form.address.complement) return false
				return true
			},
			isPhone2Valid() {
				if (this.obligations?.phone_2 && !this.form.additional_data.phone_2) return false
				if (this.form.additional_data.phone_2 && !this.ValidatePhone(this.form.additional_data.phone_2))
					return false
				return true
			},
			isPhone3Valid() {
				if (this.obligations?.phone_3 && !this.form.additional_data.phone_3) return false
				if (this.form.additional_data.phone_3 && !this.ValidatePhone(this.form.additional_data.phone_3))
					return false
				return true
			},
			isScholarshipValid() {
				if (this.obligations?.scholarship && !this.form.additional_data.scholarship) return false
				return true
			},
			isRaceValid() {
				if (this.obligations?.race && !this.form.additional_data.race) return false
				return true
			},
			isReligionValid() {
				if (this.obligations?.religion && !this.form.additional_data.religion) return false
				return true
			},
			validations() {
				const baseValidations = {
					patient_data: {
						cpf: true,
						rg: true,
						rg_uf: true,
						cns: true,
						patient_name: true,
						birth_date: true,
						sex: true,
						phone: true,
						email: true,
						mother_name: true,
					},
					address: {
						cep: true,
						street: true,
						neighborhood: true,
						city: true,
						state: true,
						complement: true,
					},
					additional_data: {
						phone_2: true,
						phone_3: true,
						scholarship: true,
						race: true,
						religion: true,
					},
				}

				if (!this.submitted) {
					return baseValidations
				}

				if (this.origin === 'pre_appointment') {
					baseValidations.patient_data.phone = this.isPhoneValid
					return baseValidations
				}

				return {
					patient_data: {
						cpf: this.isCpfValid,
						rg: this.isRgValid,
						rg_uf: this.isRgUfValid,
						cns: this.isCnsValid,
						patient_name: this.isPatientNameValid,
						birth_date: this.isBirthDateValid,
						sex: this.isSexValid,
						phone: this.isPhoneValid,
						email: this.isEmailValid,
						mother_name: this.isMotherNameValid,
					},
					address: {
						cep: this.isCepValid,
						street: this.isStreetValid,
						neighborhood: this.isNeighborhoodValid,
						city: this.isCityValid,
						state: this.isStateValid,
						complement: this.isComplementValid,
					},
					additional_data: {
						phone_2: this.isPhone2Valid,
						phone_3: this.isPhone3Valid,
						scholarship: this.isScholarshipValid,
						race: this.isRaceValid,
						religion: this.isReligionValid,
					},
				}
			},
			STATES() {
				return [...window.statesBR, { initials: 'EX' }].map(state => ({
					value: state.initials,
					label: state.initials,
				}))
			},
			isFormValid() {
				const everyPatientDataValid = Object.values(this.validations.patient_data).every(valid => valid)
				const everyAddressValid = Object.values(this.validations.address).every(valid => valid)
				const everyAdditionalDataValid = Object.values(this.validations.additional_data).every(valid => valid)

				return everyPatientDataValid && everyAddressValid && everyAdditionalDataValid
			},
		},
		watch: {
			patient: {
				handler(val, oldVal) {
					if (val && !_.isEmpty(val)) {
						const isSamePatient =
							(val.id && val.id === this.form.id) || (!val.id && !this.form.id && oldVal)

						if (isSamePatient && val.avatar !== this.form.avatar) {
							this.form.avatar = val.avatar
							return
						}

						let p = { ...val }
						delete p.schedulings

						this.form = this.ToEntity(p)
						this.form.id = p.id
						this.form.avatar = p.avatar
						this.form.patient_code = p.patient_code
						this.form.doc_id = p.doc_id
						this.form.tipo = p.tipo
						this.form.numero = p.numero
					}
				},
				immediate: true,
				deep: true,
			},
			'form.patient_data.unknown': {
				handler(val) {
					if (val) {
						this.form.patient_data.uncapable = false
						this.form.patient_data.newborn = false
					}
				},
			},
			'form.patient_data.uncapable': {
				handler(val) {
					if (val) {
						this.form.patient_data.unknown = false
						this.form.patient_data.newborn = false
					}
				},
			},
			'form.patient_data.newborn': {
				handler(val) {
					if (val) {
						this.form.patient_data.unknown = false
						this.form.patient_data.uncapable = false
					}
				},
			},
			'form.patient_data.cpf': {
				handler() {
					this.exists_doc.cpf = false
				},
				immediate: true,
			},
			'form.patient_data.rg': {
				handler() {
					this.exists_doc.rg = false
				},
				immediate: true,
			},
			'form.patient_data.rg_uf': {
				handler() {
					this.exists_doc.rg = false
				},
				immediate: true,
			},
			'form.patient_data.cns': {
				handler() {
					this.exists_doc.cns = false
				},
				immediate: true,
			},
			'form.patient_data.patient_name': {
				handler(val) {
					if (val != null) {
						this.form.patient_data.patient_name = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.patient_data.mother_name': {
				handler(val) {
					if (val != null) {
						this.form.patient_data.mother_name = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.patient_data.social_name': {
				handler(val) {
					if (val != null) {
						this.form.social_name = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.address.street': {
				handler(val) {
					if (val != null) {
						this.form.street = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.address.complement': {
				handler(val) {
					if (val != null) {
						this.form.complement = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.address.city': {
				handler(val) {
					if (val != null) {
						this.form.city = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.address.neighborhood': {
				handler(val) {
					if (val != null) {
						this.form.neighborhood = helpers.RemoveAcento(val).toUpperCase()
					}
				},
			},
			'form.avatar': {
				immediate: true,
				handler(val) {
					this.exihibit_avatar = val || ''
				},
			},
			camera(id) {
				this.deviceId = id
			},
			devices() {
				const [first, ...tail] = this.devices

				if (first) {
					this.camera = first.deviceId
					this.deviceId = first.deviceId
				}
			},
			baseName: {
				handler(val) {
					if (val) {
						this.form.patient_data.patient_name = val
					}
				},
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			ValidatePhone(phone) {
				let isValid = false

				if (phone) {
					const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$/
					isValid = phoneRegex.test(phone)
				}

				return isValid
			},
			async GetPatientsForScheduling() {
				return window.api
					.call('post', '/get-patients-for-scheduling', {
						account_id: this.requester_selected_id,
					})
					.then(({ data }) => {
						if (!data.response) {
							useError('HOUVE UM ERRO AO CARREGAR OS PACIENTES')
							return
						}

						this.$emit('patients-loaded', data.patients)

						this.patients = data.patients.map(p => {
							const [phone, phone_2, phone_3] = JSON.parse(p.telefone || '[]')

							return {
								...p,
								patient_name: p.nome_paciente,
								cns: p.sus,
								telefone_1: phone,
								phone,
								phone_2,
								phone_3,
								unknown: p.desconhecido,
							}
						})
					})
			},
			async ResetExistsPatientDocs() {
				this.exists_doc = {
					cpf: false,
					rg: false,
					cns: false,
				}

				return this.exists_doc
			},
			async Continue() {
				await this.ResetExistsPatientDocs()

				this.submitted = true

				if (!this.isFormValid) {
					const hasOpenedGroups = await this.OpenInvalidGroups()
					await this.$nextTick()
					const firstError = this.$el.querySelector('.field-error, .vs--error, .date-wrapper--error')
					if (firstError) {
						setTimeout(
							() => {
								firstError.scrollIntoView({
									behavior: 'smooth',
									block: 'center',
								})
							},
							hasOpenedGroups ? 250 : 1
						)
					}
					return
				}

				const { cpf, rg, cns } = await this.CheckPatientDocs()
				if (cpf || rg || cns) {
					const existent_docs = Object.entries(this.exists_doc)
						.filter(d => d[1])
						.map(d => d[0].toUpperCase())

					let docs_html = `O DOCUMENTO <b>(${existent_docs[0]})</b> JÁ ESTÁ VINCULADO À OUTRO PACIENTE. SE CONTINUAR, PODERÁ GERAR UMA DUPLICIDADE DE PRONTUÁRIO!`

					if (existent_docs.length > 1) {
						docs_html = `OS DOCUMENTOS <b>(${existent_docs.join(
							', '
						)})</b> JÁ ESTÃO VINCULADOS À OUTRO PACIENTE. SE CONTINUAR, PODERÁ GERAR UMA DUPLICIDADE DE PRONTUÁRIO!`
					}

					docs_html += `<br><br>DESEJA CONTINUAR?`

					Swal.fire({
						title: 'ATENÇÃO!',
						html: docs_html,
						confirmButtonText: 'SIM',
						showDenyButton: true,
						denyButtonText: 'NÃO',
						reverseButtons: true,
						customClass: {
							container: 'themed invalid',
						},
					}).then(async result => {
						if (result.isConfirmed) {
							await this.SavePatient()
						}
					})
				} else {
					await this.SavePatient()
				}
			},
			async CheckPatientDocs() {
				this.StartLoading()

				return await window.api
					.call('post', '/check-patients-docs', {
						organization_id: this.requester_selected_id,
						patient_id: this.form.id,
						cpf: this.form.patient_data.cpf,
						rg: this.form.patient_data.rg,
						rg_uf: this.form.patient_data.rg_uf,
						cns: this.form.patient_data.cns,
					})
					.then(({ data }) => {
						if (data.response) {
							this.exists_doc = data.docs

							return this.exists_doc
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async OpenInvalidGroups() {
				let hasOpenedGroups = false

				for (const group in this.validations) {
					for (const field in this.form[group]) {
						if (this.validations[group][field] === false) {
							hasOpenedGroups = true
							this.open[group] = true
						}
					}
				}

				return hasOpenedGroups
			},
			async SavePatient() {
				this.StartLoading()

				const formData = new FormData()

				let obj = {
					account_id: this.requester_selected_id,
					form_type: this.formType,
					...this.ToDataMap(),
				}

				if (obj.id) {
					obj.patient_id = obj.id

					delete obj.id
				}

				Object.keys(obj).map((key, index) => {
					if (key != 'avatar' && obj[key]) {
						formData.append(key, obj[key])
					}
				})

				let avatarSaved = false

				if (this.form.avatar instanceof File) {
					formData.append('avatar', this.form.avatar, 'avatar.jpg')
				} else if (this.form.avatar) {
					if (this.form.avatar.indexOf('http') > -1) {
						avatarSaved = true
					}

					if (!avatarSaved) {
						let avatar = this.form.avatar.replace('data:image/jpeg;base64,', '')
						avatar = this.form.avatar.replace('data:image/png;base64,', '')
						avatar = this.Base64ToBlob(avatar, 'image/jpeg')

						formData.append('avatar', avatar, 'avatar.jpg')
					}
				}

				await window.api
					.call('post', '/save-patient', formData)
					.then(({ data }) => {
						if (!data.response) {
							useError('HOUVE UM ERRO AO SALVAR O PACIENTE.')
							return
						}

						this.ResetExistsPatientDocs()

						this.$emit('next', {
							patient: {
								...data.patient,
								rh: data.patient.rh?.formatted,
							},
						})

						useConfirmation()
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async SearchZipCode() {
				const vm = this
				let cep = vm.form.cep

				if (cep.length == 9) {
					vm.StartLoading()

					helpers
						.searchZipCode(cep)
						.then(res => {
							vm.form.street = res.address
							vm.form.neighborhood = res.district
							vm.form.complement = res.complement
							vm.form.city = res.city
							vm.form.state = res.state
						})
						.finally(() => {
							vm.FinishLoading()
						})
				}
			},
			Base64ToBlob(base64, mime) {
				mime = mime || ''
				let sliceSize = 1024
				let byteChars = window.atob(base64)
				let byteArrays = []

				for (let offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
					let slice = byteChars.slice(offset, offset + sliceSize)

					let byteNumbers = new Array(slice.length)

					for (let i = 0; i < slice.length; i++) {
						byteNumbers[i] = slice.charCodeAt(i)
					}

					let byteArray = new Uint8Array(byteNumbers)

					byteArrays.push(byteArray)
				}

				return new Blob(byteArrays, { type: mime })
			},
			async ShowPhotoModal() {
				const vm = this

				window.jQuery('#modal-webcam').modal({
					backdrop: false,
					show: true,
				}) //.on('shown.bs.modal', () => {

				vm.webcam.ready = true

				vm.$refs.webcam.start()

				//GAMBITECH
				window.jQuery('body').addClass('modal-open')
				//})

				vm.webcam.snap = vm.form.avatar ?? null
			},
			SnapPhoto() {
				if (this.webcam.snap) {
					this.webcam.snap = null
					this.$refs.webcam.start()
					return
				}

				this.webcam.snap = this.$refs.webcam.capture()
			},
			async ClosePhotoModal() {
				const vm = this

				vm.form.avatar = vm.webcam.snap || vm.form.avatar
				vm.exihibit_avatar = vm.form.avatar

				window
					.jQuery('#modal-webcam')
					.modal('hide')
					.on('hidden.bs.modal', () => {
						vm.$refs.webcam.stop()
						vm.webcam.ready = false

						//GAMBITECH
						if (vm.formType == 'pre' || vm.formType == 'standard') {
							window.jQuery('body').addClass('modal-open')
						}
					})
			},
			onCameras(cameras) {
				this.devices = cameras
			},
			onCameraChange(deviceId) {
				this.deviceId = deviceId
			},
			UploadAvatar(event) {
				if (event.target.files) {
					const reader = new FileReader()
					const file = event.target.files[0]

					if (file) {
						this.form.avatar = file

						reader.onload = e => (this.exihibit_avatar = e.target.result)

						reader.readAsDataURL(file)
					}
				}

				document.querySelector('#input-avatar').click()
			},
			async SelectPatient(patientId) {
				this.ClearForm()

				const { patient } = await this.GetPatient(patientId)
				if (patient) {
					this.form = this.ToEntity(patient)
					this.form.id = patient.id
					this.form.avatar = patient.avatar
					this.form.patient_code = patient.patient_code
					this.form.doc_id = patient.doc_id
					this.form.tipo = patient.tipo
					this.form.numero = patient.numero

					this.$emit('selected', patient)
				}
			},
			async GetPatient(patientId) {
				this.StartLoading()

				return window.api
					.call('post', '/get-patient', {
						account_id: this.requester_selected_id,
						patient_id: patientId,
					})
					.then(({ data }) => {
						if (!data.response) {
							useError('HOUVE UM ERRO AO CARREGAR O PACIENTE.')
							return
						}

						return { patient: data.patient }
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			ToEntity(patient) {
				if (patient.telefone) {
					let telefone = JSON.parse(patient.telefone)
					if (Array.isArray(telefone) && telefone.length > 0) {
						telefone.forEach((t, index) => {
							patient[`telefone_${index + 1}`] = t
						})
					}
				} else {
					patient.telefone_1 = ''
				}

				return {
					patient_data: {
						unknown: patient.desconhecido,
						uncapable: patient.uncapable,
						newborn: patient.newborn,
						rh: patient.rh,
						cpf: window.helpers.ToCpf(patient.cpf),
						rg: patient.rg,
						rg_uf: patient.rg_uf,
						cns: patient.sus,
						patient_name: patient.nome_paciente,
						birth_date: patient.data_nascimento,
						sex: patient.sexo,
						phone: patient.telefone_1,
						email: patient.email,
						social_name: patient.nome_social,
						genre: patient.genero,
						mother_name: patient.nome_mae,
					},
					address: {
						cep: patient.cep,
						street: patient.logradouro,
						neighborhood: patient.bairro,
						city: patient.municipio,
						state: patient.estado,
						complement: patient.complemento,
					},
					additional_data: {
						phone_2: patient.telefone_2,
						phone_3: patient.telefone_3,
						scholarship: patient.escolaridade,
						race: patient.raca,
						religion: patient.religiao,
						observation: patient.observacao,
					},
				}
			},
			ToDataMap() {
				return {
					id: this.form.id,
					patient_code: this.form.patient_code,
					// origem: this.form.origem,
					avatar: this.form.avatar,

					desconhecido: this.form.patient_data.unknown,
					uncapable: this.form.patient_data.uncapable,
					newborn: this.form.patient_data.newborn,
					rh: this.form.patient_data.rh,
					cpf: this.form.patient_data.cpf,
					rg: this.form.patient_data.rg,
					rg_uf: this.form.patient_data.rg_uf,
					sus: this.form.patient_data.cns,
					nome_paciente: this.form.patient_data.patient_name,
					data_nascimento: this.form.patient_data.birth_date,
					sexo: this.form.patient_data.sex,
					telefone_1: this.form.patient_data.phone,
					telefone_2: this.form.additional_data.phone_2,
					telefone_3: this.form.additional_data.phone_3,
					email: this.form.patient_data.email,
					nome_social: this.form.patient_data.social_name,
					genero: this.form.patient_data.genre,
					nome_mae: this.form.patient_data.mother_name,

					cep: this.form.address.cep,
					logradouro: this.form.address.street,
					bairro: this.form.address.neighborhood,
					municipio: this.form.address.city,
					estado: this.form.address.state,
					complemento: this.form.address.complement,

					escolaridade: this.form.additional_data.scholarship,
					raca: this.form.additional_data.race,
					religiao: this.form.additional_data.religion,
					observacao: this.form.additional_data.observation,
				}
			},
			ClearForm() {
				this.form.id = null
				this.form.patient_code = ''
				this.form.origem = ''
				this.form.avatar = ''
				this.form.doc_id = null
				this.form.tipo = ''
				this.form.numero = ''

				this.form.patient_data.unknown = false
				this.form.patient_data.uncapable = false
				this.form.patient_data.newborn = false
				this.form.patient_data.cpf = ''
				this.form.patient_data.rg = ''
				this.form.patient_data.rg_uf = ''
				this.form.patient_data.cns = ''
				this.form.patient_data.rh = ''
				this.form.patient_data.patient_name = ''
				this.form.patient_data.birth_date = ''
				this.form.patient_data.sex = ''
				this.form.patient_data.phone = ''
				this.form.patient_data.email = ''
				this.form.patient_data.social_name = ''
				this.form.patient_data.genre = ''
				this.form.patient_data.mother_name = ''

				this.form.address.cep = ''
				this.form.address.street = ''
				this.form.address.neighborhood = ''
				this.form.address.city = ''
				this.form.address.state = ''
				this.form.address.complement = ''

				this.form.additional_data.phone_2 = ''
				this.form.additional_data.phone_3 = ''
				this.form.additional_data.scholarship = ''
				this.form.additional_data.race = ''
				this.form.additional_data.religion = ''
				this.form.additional_data.observation = ''
			},
			OpenGroups() {
				for (const group in this.form) {
					this.OpenGroup(group)
				}
			},
			async OpenGroup(group) {
				for (const field in this.form[group]) {
					const isRequired = this.obligations?.[field] === true
					const isFilled = this.form[group][field] !== null && this.form[group][field] !== ''
					if (isRequired || isFilled) {
						this.open[group] = true
						break
					}
				}
			},
			LoadedHandler() {},
		},
		beforeMount() {
			if (this.withQuery) this.GetPatientsForScheduling()
		},
		mounted() {
			this.OpenGroups()
			this.$emit('loaded')
		},
	}
</script>

<style lang="scss" scoped>
	#img-avatar {
		cursor: pointer;
	}

	#webcam,
	#img-snapped,
	#img-avatar {
		width: 100%;
	}

	#close-modal {
		position: absolute;
		right: 15px;
		top: 15px;
	}

	@media (min-width: 575.98px) {
		.modal-content {
			width: 75% !important;
		}
	}

	@media (min-width: 1000px) {
		.modal-content {
			max-width: 50% !important;
		}
	}

	@media (min-width: 1500px) {
		.modal-content {
			max-width: 800px !important;
		}
	}

	.placeholder-select {
		color: var(--fontColorSecondary);
	}
</style>
